import React from "react";
import { useLocation, useNavigate } from "react-router";

import type { CreateTaskSubmission } from "~/components/createTaskModal";
import type { ResolveCallSubmission } from "~/components/resolveCallModal";
import type { CallState } from "~/context/call/callState";
import {
  isSuperviseCallSession,
  shouldAutoOpenCallResolution,
  shouldOwnCallResolutionSession,
} from "~/context/call/callResolutionPolicy";
import { shouldSuppressCallResolutionForTransfer } from "~/context/call/callState";
import { createCustomerTask as createCustomerTaskApi } from "~/services/customerService";
import { endCallLog, resolveCallLog } from "~/services/callService";
import { getApiErrorMessage } from "~/services/apiErrors";
import {
  createDiffField,
  loadCustomerInteractions,
  saveCustomerInteractions,
  type CustomerActivityRecord,
  type CustomerTaskRecord,
} from "~/services/customerInteractionService";

export type CallResolutionContext = {
  customerId: string;
  callUuid?: string | null;
  callReference?: string | null;
  callerNumber?: string | null;
  customerName?: string | null;
};

export type CallResolutionActions = {
  beginCallResolution: () => void;
  openResolutionModal: () => void;
  deferResolution: () => void;
  deferResolutionAndNavigateDashboard: () => void;
  submitResolution: (resolution: ResolveCallSubmission) => Promise<void>;
  submitFollowUpAndFinalize: (payload: CreateTaskSubmission) => Promise<void>;
};

type UseCallResolutionParams = {
  state: CallState;
  stateSnapshotRef: React.MutableRefObject<CallState>;
  activeCustomerId: string | null;
  hangup: () => Promise<void>;
  updateState: (next: Partial<CallState>) => void;
  operatorId?: string | null;
  operatorName?: string | null;
};

const RESOLVED_CALL_SESSION_RESET: Partial<CallState> = {
  callStatus: "ended",
  callStartedAt: null,
  callElapsedSeconds: 0,
  isMuted: false,
  isOnHold: false,
  conferenceRoom: null,
  participants: [],
  activeSuperviseMode: null,
  bargePromotionNotice: null,
  pendingBargeTransfer: null,
  bargeTransferNotice: null,
  backgroundHeldCallUuid: null,
  backgroundHeldCallReference: null,
  heldCallerNumber: null,
  consultNumber: null,
  consultStatus: null,
};

function isLiveCallStatus(status: CallState["callStatus"]) {
  return (
    status === "ringing" ||
    status === "dialing" ||
    status === "active" ||
    status === "held"
  );
}

function readCustomerIdFromCallerDetailsUrl(pathname: string, search: string) {
  if (pathname !== "/caller-details") return null;
  const customerId = new URLSearchParams(search).get("customerId")?.trim();
  return customerId || null;
}

export const buildFollowUpTitle = (resolution: ResolveCallSubmission | null) => {
  if (!resolution) return "";
  if (resolution.notes) {
    return `Follow up: ${resolution.notes}`;
  }
  return `Follow up: ${resolution.category}`;
};

export const useCallResolution = ({
  state,
  stateSnapshotRef,
  activeCustomerId,
  hangup,
  updateState,
  operatorId,
  operatorName,
}: UseCallResolutionParams) => {
  const navigate = useNavigate();
  const location = useLocation();
  const finalizingResolveRef = React.useRef(false);
  const previousCallStatusRef = React.useRef(state.callStatus);
  const suppressAutoOpenRef = React.useRef(false);
  const resolutionFinalizedRef = React.useRef(false);
  const resolutionContextRef = React.useRef<CallResolutionContext | null>(null);
  const followUpTaskCreatedForRef = React.useRef<ResolveCallSubmission | null>(null);
  const ownedCallSessionRef = React.useRef(false);
  const supervisedCallSessionRef = React.useRef(false);

  const [resolutionContext, setResolutionContext] =
    React.useState<CallResolutionContext | null>(null);
  const [resolutionModalOpen, setResolutionModalOpen] = React.useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = React.useState(false);
  const [pendingResolution, setPendingResolution] =
    React.useState<ResolveCallSubmission | null>(null);
  const [resolutionError, setResolutionError] = React.useState<string | null>(
    null
  );

  const customerIdFromUrl = React.useMemo(
    () => readCustomerIdFromCallerDetailsUrl(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const resolveCustomerId = React.useCallback(() => {
    return (
      resolutionContextRef.current?.customerId ??
      resolutionContext?.customerId ??
      state.customerId ??
      activeCustomerId ??
      customerIdFromUrl ??
      null
    );
  }, [
    activeCustomerId,
    customerIdFromUrl,
    resolutionContext?.customerId,
    state.customerId,
  ]);

  const buildResolutionContext = React.useCallback((): CallResolutionContext | null => {
    const customerId = resolveCustomerId();
    if (!customerId) return null;

    return {
      customerId,
      callUuid: state.callUuid ?? state.callId,
      callReference: state.callReference,
      callerNumber: state.callerNumber,
      customerName: state.customerName,
    };
  }, [
    resolveCustomerId,
    state.callId,
    state.callReference,
    state.callUuid,
    state.callerNumber,
    state.customerName,
  ]);

  const applyResolutionContext = React.useCallback(
    (context: CallResolutionContext | null) => {
      resolutionContextRef.current = context;
      setResolutionContext(context);
    },
    []
  );

  const clearResolutionState = React.useCallback(() => {
    setResolutionModalOpen(false);
    setFollowUpModalOpen(false);
    setPendingResolution(null);
    applyResolutionContext(null);
    setResolutionError(null);
  }, [applyResolutionContext]);

  const resetActiveCallSession = React.useCallback(() => {
    updateState(RESOLVED_CALL_SESSION_RESET);
  }, [updateState]);

  const navigateToDashboard = React.useCallback(() => {
    navigate("/dashboard", { replace: true });

    if (typeof window === "undefined") return;

    window.requestAnimationFrame(() => {
      if (window.location.pathname.startsWith("/caller-details")) {
        window.location.assign("/dashboard");
      }
    });
  }, [navigate]);

  const persistResolutionRecord = React.useCallback(
    async (resolution: ResolveCallSubmission, customerId: string) => {
      const current = loadCustomerInteractions(customerId);
      const diffs = [
        createDiffField("Category", "", resolution.category),
        resolution.notes
          ? createDiffField("Resolution Notes", "", resolution.notes)
          : null,
        createDiffField(
          "Follow Up",
          "No",
          resolution.followUpRequired ? "Yes" : "No"
        ),
      ].filter(Boolean) as NonNullable<CustomerActivityRecord["diff"]>;
      const activity: CustomerActivityRecord = {
        id: `activity-${resolution.timestampMs}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        timestampMs: resolution.timestampMs,
        message: `Call resolved: ${resolution.category}`,
        diff: diffs.length > 0 ? diffs : undefined,
        source: "ui",
      };

      saveCustomerInteractions(customerId, {
        notes: current.notes,
        tasks: current.tasks,
        activity: [activity, ...current.activity],
      });
    },
    []
  );

  const createFollowUpTask = React.useCallback(
    async (
      { title, scheduledAtMs }: CreateTaskSubmission,
      customerId: string
    ) => {
      const now = Date.now();
      let taskId = `task-${now}`;
      let createdAtMs = now;
      let updatedAtMs = now;

      const createdTask = await createCustomerTaskApi(customerId, {
        title,
        scheduledAt: scheduledAtMs,
      });
      taskId = createdTask.id;
      const createdAt = new Date(createdTask.createdAt).getTime();
      const updatedAt = new Date(createdTask.updatedAt).getTime();
      if (Number.isFinite(createdAt)) createdAtMs = createdAt;
      if (Number.isFinite(updatedAt)) updatedAtMs = updatedAt;

      const current = loadCustomerInteractions(customerId);
      const followUpTask: CustomerTaskRecord = {
        id: taskId,
        title,
        scheduledAtMs,
        completed: false,
        createdAtMs,
        updatedAtMs,
        ...(operatorId ? { createdByOperatorId: operatorId } : {}),
        ...(operatorName ? { createdByOperatorName: operatorName } : {}),
      };
      const diffs = [
        createDiffField("Task", "", title),
        createDiffField(
          "Scheduled",
          "",
          new Date(scheduledAtMs).toLocaleString()
        ),
      ].filter(Boolean) as NonNullable<CustomerActivityRecord["diff"]>;
      const taskActivity: CustomerActivityRecord = {
        id: `activity-${now}-${Math.random().toString(36).slice(2, 8)}`,
        timestampMs: now,
        message: "Task created",
        diff: diffs.length > 0 ? diffs : undefined,
        source: "ui",
      };

      saveCustomerInteractions(customerId, {
        notes: current.notes,
        tasks: [followUpTask, ...current.tasks],
        activity: [taskActivity, ...current.activity],
      });
    },
    [operatorId, operatorName]
  );

  const finalizeCallResolution = React.useCallback(
    async (resolution: ResolveCallSubmission) => {
      if (finalizingResolveRef.current) return;

      const snapshot = stateSnapshotRef.current;
      const context =
        resolutionContextRef.current ??
        buildResolutionContext() ??
        resolutionContext;
      const customerId = context?.customerId ?? resolveCustomerId();
      const callUuidForLog =
        context?.callUuid ??
        snapshot.callUuid ??
        snapshot.callId ??
        null;
      const wasLive = isLiveCallStatus(snapshot.callStatus);

      finalizingResolveRef.current = true;
      setResolutionError(null);

      try {
        resolutionFinalizedRef.current = true;
        suppressAutoOpenRef.current = true;

        if (wasLive) {
          await hangup().catch(() => {});
        }

        // The category and notes used to stay in this browser only, so the alarm report and
        // the communication history never had them (AC-408). They are saved on the call before
        // the form closes: if that fails the operator sees why and can confirm again.
        if (callUuidForLog) {
          await resolveCallLog(callUuidForLog, resolution);
        }

        clearResolutionState();
        navigateToDashboard();

        if (customerId) {
          try {
            await persistResolutionRecord(resolution, customerId);
          } catch (error) {
            console.warn("Unable to persist local call resolution record.", error);
          }
        }

        resetActiveCallSession();

        if (callUuidForLog) {
          try {
            await endCallLog(callUuidForLog, "RESOLVED_BY_OPERATOR");
          } catch (error) {
            console.warn("Unable to update backend call end status.", error);
          }
        }
      } catch (error) {
        resolutionFinalizedRef.current = false;
        suppressAutoOpenRef.current = false;
        const parsed = getApiErrorMessage(
          error,
          "Unable to finalize call resolution."
        );
        setResolutionError(parsed.message);
        throw error;
      } finally {
        finalizingResolveRef.current = false;
      }
    },
    [
      buildResolutionContext,
      clearResolutionState,
      hangup,
      navigateToDashboard,
      persistResolutionRecord,
      resetActiveCallSession,
      resolutionContext,
      resolveCustomerId,
      stateSnapshotRef,
    ]
  );

  const beginCallResolution = React.useCallback(() => {
    const context = buildResolutionContext();
    if (context) {
      applyResolutionContext(context);
    }
    setResolutionError(null);
    setResolutionModalOpen(true);
  }, [applyResolutionContext, buildResolutionContext]);

  const openResolutionModal = React.useCallback(() => {
    const context = buildResolutionContext();
    if (context) {
      applyResolutionContext(context);
    }
    setResolutionError(null);
    setResolutionModalOpen(true);
  }, [applyResolutionContext, buildResolutionContext]);

  const deferResolution = React.useCallback(() => {
    setResolutionModalOpen(false);
    setFollowUpModalOpen(false);
  }, []);

  const deferResolutionAndNavigateDashboard = React.useCallback(() => {
    suppressAutoOpenRef.current = true;
    deferResolution();
    navigateToDashboard();
  }, [deferResolution, navigateToDashboard]);

  const submitResolution = React.useCallback(
    async (resolution: ResolveCallSubmission) => {
      const context = buildResolutionContext();
      if (context) {
        applyResolutionContext(context);
      }

      if (resolution.followUpRequired) {
        setPendingResolution(resolution);
        setResolutionModalOpen(false);
        setFollowUpModalOpen(true);
        return;
      }

      await finalizeCallResolution(resolution);
    },
    [applyResolutionContext, buildResolutionContext, finalizeCallResolution]
  );

  const submitFollowUpAndFinalize = React.useCallback(
    async (payload: CreateTaskSubmission) => {
      if (!pendingResolution) {
        throw new Error("Resolve call context was lost. Please try again.");
      }

      // Confirming again after the resolution failed to save must not create the task twice.
      const customerId = resolveCustomerId();
      if (customerId && followUpTaskCreatedForRef.current !== pendingResolution) {
        await createFollowUpTask(payload, customerId);
        followUpTaskCreatedForRef.current = pendingResolution;
      }

      await finalizeCallResolution(pendingResolution);
    },
    [
      createFollowUpTask,
      finalizeCallResolution,
      pendingResolution,
      resolveCustomerId,
    ]
  );

  React.useEffect(() => {
    if (isSuperviseCallSession(state)) {
      supervisedCallSessionRef.current = true;
      // Clear any ownership latched during the call:ended / SIP teardown race.
      ownedCallSessionRef.current = false;
      return;
    }

    const correlator = state.callUuid?.trim() || state.callId?.trim() || null;
    const matching = correlator
      ? state.answeredCalls.find((call) => call.uniqueId === correlator)
      : undefined;
    // Barge promotion / takeover: this operator now owns the live call.
    if (matching?.isMine === true) {
      supervisedCallSessionRef.current = false;
    }

    // Once a session was supervise, never re-latch ownership from the
    // incoming fallback after answeredCalls / activeSuperviseMode are cleared.
    if (
      !supervisedCallSessionRef.current &&
      shouldOwnCallResolutionSession(state)
    ) {
      ownedCallSessionRef.current = true;
    }
  }, [
    state.activeSuperviseMode,
    state.answeredCalls,
    state.callDirection,
    state.callId,
    state.callStatus,
    state.callUuid,
  ]);

  React.useEffect(() => {
    const previousStatus = previousCallStatusRef.current;
    const wasLive = isLiveCallStatus(previousStatus);
    const isLive = isLiveCallStatus(state.callStatus);

    if (isLive && !wasLive) {
      resolutionFinalizedRef.current = false;
      suppressAutoOpenRef.current = false;
      ownedCallSessionRef.current = false;
      supervisedCallSessionRef.current = false;
    }

    if (
      resolutionFinalizedRef.current ||
      suppressAutoOpenRef.current ||
      resolutionModalOpen ||
      followUpModalOpen
    ) {
      previousCallStatusRef.current = state.callStatus;
      return;
    }

    if (wasLive && !isLive) {
      const correlator =
        state.callUuid?.trim() || state.callId?.trim() || null;

      if (shouldSuppressCallResolutionForTransfer(state, correlator)) {
        previousCallStatusRef.current = state.callStatus;
        return;
      }

      const shouldOpen = shouldAutoOpenCallResolution({
        previousCallStatus: previousStatus,
        ownedCallSession: ownedCallSessionRef.current,
        supervisedCallSession: supervisedCallSessionRef.current,
      });

      if (shouldOpen) {
        const context = buildResolutionContext();
        if (context) {
          applyResolutionContext(context);
          setResolutionModalOpen(true);
          setResolutionError(null);
        }
      }

      ownedCallSessionRef.current = false;
      supervisedCallSessionRef.current = false;
    }

    previousCallStatusRef.current = state.callStatus;
  }, [
    applyResolutionContext,
    buildResolutionContext,
    followUpModalOpen,
    resolutionModalOpen,
    state.bargeTransferNotice,
    state.callId,
    state.callStatus,
    state.callUuid,
    state.pendingBargeTransfer,
  ]);

  const resolutionActions = React.useMemo<CallResolutionActions>(
    () => ({
      beginCallResolution,
      openResolutionModal,
      deferResolution,
      deferResolutionAndNavigateDashboard,
      submitResolution,
      submitFollowUpAndFinalize,
    }),
    [
      beginCallResolution,
      deferResolution,
      deferResolutionAndNavigateDashboard,
      openResolutionModal,
      submitFollowUpAndFinalize,
      submitResolution,
    ]
  );

  return {
    resolutionContext,
    resolutionModalOpen,
    followUpModalOpen,
    pendingResolution,
    resolutionError,
    resolutionActions,
    buildFollowUpTitle,
  };
};
